"use client";

import type { DropdownLinkListItem } from "@homeinstead/schemas";

import { ChevronDown } from "lucide-react";
import Image from "next/image";
import React, { useMemo } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../Select";

type LinkItem = {
  _key: string;
  fullPath: string | null;
  iconUrl?: string | null;
} & Pick<DropdownLinkListItem, "label" | "isDefault" | "openInNewTab">;

interface LinkSelectProps {
  /** Optional label for the dropdown */
  label?: string;
  /** Placeholder text when no option is selected */
  placeholder?: string;
  /** Array of link items to display in the dropdown */
  items: LinkItem[];
}

export const LinkSelect = ({ label, placeholder, items }: LinkSelectProps) => {
  const defaultValue = useMemo(
    () => items.find((item) => item.isDefault),
    [items],
  );

  const onValueChange = (value: string) => {
    const item = items.find((item) => item._key === value);

    if (!item || !item.fullPath) {
      return;
    }

    window.open(
      item.fullPath,
      item.openInNewTab ? "_blank" : "_self",
      item.openInNewTab ? "noopener,noreferrer" : undefined,
    );
  };

  return (
    <Select defaultValue={defaultValue?._key} onValueChange={onValueChange}>
      <SelectTrigger
        aria-label="Link Selector"
        chevronIcon={
          <ChevronDown
            className="h-4 w-4 transition-transform duration-200"
            strokeWidth={4}
          />
        }
        className="rounded-lg border-2"
        label={label}
      >
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {items.map((option) => (
          <SelectItem key={option._key} value={option._key}>
            {option.iconUrl && (
              <Image
                alt=""
                className="mr-2 inline-block"
                height={15}
                src={option.iconUrl}
                width={20}
              />
            )}
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};
